@use '@/styles/tokens' as *;

.root {
  display: flex;
  flex-direction: column;
  gap: 12px;
  width: 100%;
}

.dragger {
  :global(.ant-upload) {
    border-radius: 10px;
  }
}

.error :global(.ant-upload.ant-upload-drag) {
  border-color: $color-error-900;
}

.empty {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
  padding: 24px;
  color: var(--text-muted);
}

.help {
  margin: 0;
  font-size: 14px;
  text-align: center;
  white-space: pre-line;
}

.grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(96px, 1fr));
  gap: 12px;
}

.tile {
  position: relative;
  aspect-ratio: 1;
  overflow: hidden;
  border: 1px solid var(--border-subtle);
  border-radius: 10px;
  background: var(--surface-muted);

  // Figma 12:163615 sizes each thumbnail's image to fit *inside* its 95px tile — a 94.97×76.25
  // landscape shot and a 69.7×86.8 portrait one both sit whole on the tinted square, with a few
  // pixels of it showing around the tight axis. `cover` filled the tile and cropped the product
  // out of frame instead (QA 119717), which is also why the tile is tinted at all: the padding is
  // meant to be seen.
  img {
    width: 100%;
    height: 100%;
    padding: var(--space-1);
    object-fit: contain;
  }
}

.tileRemove {
  position: absolute;
  top: 4px;
  right: 4px;
}

.tileLoading {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  height: 100%;
  font-size: 12px;
  color: var(--text-muted);
}

.list {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

// Figma 12:163611 — a tinted 56px pill, not a bordered card: the glyph sits in its own
// near-white 48px tile, the name/size pair is 13/12px, and the trash closes the row.
.row {
  display: flex;
  align-items: center;
  gap: 9px;
  // antd centres everything inside the drop zone; Figma 12:163611 left-aligns the name and
  // size. Set here rather than unset on the dragger, so the list reads the same whether it
  // sits inside the collapsed zone or below an empty one.
  text-align: left;
  min-height: 56px;
  padding: 4px 12px 4px 6px;
  border-radius: 10px;
  background: var(--surface-subtle);
}

.rowIcon {
  display: flex;
  flex-shrink: 0;
  align-items: center;
  justify-content: center;
  width: 48px;
  height: 48px;
  border-radius: 10px;
  background: $color-light-green-50;
  color: var(--text-secondary);
}

.rowBody {
  display: flex;
  flex: 1 0 0;
  min-width: 0;
  flex-direction: column;
  gap: var(--space-1);
}

.rowName {
  font-size: 13px;
  color: var(--text-secondary);
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.rowMeta {
  font-size: 12px;
  color: var(--text-tertiary);
}

.bar {
  height: 4px;
  margin-top: 6px;
  border-radius: 999px;
  background: var(--surface-muted);

  span {
    display: block;
    height: 100%;
    border-radius: inherit;
    background: $color-neutral-950;
    transition: width 0.2s ease;
  }
}

.rowError {
  font-size: 12px;
  color: $color-error-900;
}

.retry,
.rowRemove {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 0;
  border: none;
  background: none;
  color: var(--text-secondary);
  cursor: pointer;
}

// Filled layout only — the empty drop zone keeps antd's default surface so the
// five existing upload zones are pixel-identical.
.draggerFilled :global(.ant-upload.ant-upload-drag) {
  background: var(--surface-card);

  // antd centres the drop zone's content with `display: table` + `table-cell`, and an
  // auto-layout table is sized by its content's min-content width — which for a document
  // row is the whole un-truncated file name, so the table grew past its own `width: 100%`
  // and the rows spilled out of the dashed box (ADO 113313). Blocks take the width they
  // are given, which lets `.rowBody`'s `min-width: 0` do the ellipsising it was always
  // meant to. Scoped to the filled layout: the empty zone still needs antd's centring.
  :global(.ant-upload-btn) {
    display: block;
  }

  :global(.ant-upload-drag-container) {
    display: block;
  }
}

.filled {
  display: flex;
  width: 100%;
  flex-direction: column;
  align-items: center;
  gap: var(--space-2);
  // Horizontal only — antd's own .ant-upload-drag padding supplies the vertical rhythm,
  // the same way the .empty drop zone above relies on it.
  padding: 0 var(--space-4);

  > .grid,
  > .list {
    width: 100%;
    margin-bottom: var(--space-2);
  }

  // Four across by default — Figma 429:272577 puts four ~79.6px tiles on one row, and at the
  // 464px drawer's 382px grid width four columns with a 16px gap give 83.5px. A caller whose box
  // is a different width says so with `columns` (the device form's own frame, Figma 12:163611,
  // draws three) rather than this rule guessing from a width it cannot see. `minmax(0, 1fr)`
  // (not 96px) so narrow viewports shrink instead of overflowing. `.filled` only exists under
  // `collapseWhenFilled`, so the catalog zones are untouched.
  > .grid {
    grid-template-columns: repeat(var(--upload-grid-columns, 4), minmax(0, 1fr));
    gap: var(--space-4);

    // A rejection's message spans the row rather than being squeezed into an 83px square, where
    // this story's longest ("Only JPG, JPEG or PNG files are supported") wrapped to five lines and
    // ran under the dismiss badge. Full width gives it one or two lines clear of the badge.
    // Scoped to `.filled`, so the four catalog upload zones keep their square error tile.
    .tileRejected {
      grid-column: 1 / -1;
      aspect-ratio: auto;
      min-height: 40px;
    }

    // Rejection copy is centred and tight-leaded. Scoped here, not on `.tileLoading` itself, which
    // also carries the percent readout and the Retry label in those catalog zones.
    .tileLoading {
      line-height: $line-height-tight;
      text-align: center;
    }
  }
}

.counter {
  display: flex;
  align-items: center;
  gap: var(--space-1);
  margin: 0;
  // 11px has no $font-size-* token; raw 11px is the convention across 12 shipped modules.
  font-size: 11px;
  color: var(--text-muted);

  svg {
    flex-shrink: 0;
    color: $color-success-900;
  }
}

/* A file refused up front (wrong type, too large, over the count). Rendered under the dropzone,
   matching `<FormField>`'s own error line exactly — the design QA asked for on ADO 119326 — so
   the two never look like two different kinds of problem. */
.rejections {
  display: flex;
  flex-direction: column;
  gap: var(--space-1);
}

.rejection {
  margin: 0;
  font-size: 11px;
  font-weight: 300;
  color: $color-error-900;
}
